import { DEFAULT_TRACK_ID, getCustomTrackLayout, getTrackLength, projectToTrack, sampleTrack } from "./track";
import { customTrackProgressToDistance } from "./customTrackLayout";
import type { DriverInput, ItemBoxState, ItemKind, KartState, RaceState } from "./types";
import type { RaceVariantState } from "./types";

const ITEM_RESPAWN = 4.5;
const ITEM_ROW_PROGRESS = [0.045, 0.29, 0.54, 0.79] as const;
const ITEM_LANES = [-7, -3.5, 0, 3.5, 7] as const;

// The three newer items are deliberately bounded and readable.  Bottle is a
// quick, narrow threat rather than another long-lived homing keg; Hiccups is a
// deterministic control-space debuff; Lime Wedge is the clean defensive
// answer that can still be used while a kart is spinning.
export const BOTTLE_SPEED = 78;
export const BOTTLE_HIT_RADIUS = 1.9;
export const BOTTLE_SPIN_TIME = 0.85;
export const BOTTLE_IMMUNITY_TIME = 2;
export const BOTTLE_MAX_TRAVEL_FRACTION = 0.25;
export const BOTTLE_MAX_BOUNCES = 2;
export const HICCUPS_DURATION = 3.5;
export const HICCUPS_SPEED_CAP = 0.82;
export const HICCUPS_MAX_TARGET_FRACTION = 0.25;
export const LIME_BOOST_TIME = 2.6;
export const LIME_DODGE_TIME = 0.52;
export const LIME_IMMUNITY_TIME = 1.25;
export const KEG_HOMING_SPEED = 62;
export const KEG_LIFETIME = 4;
export const KEG_MAX_TARGET_DISTANCE = KEG_HOMING_SPEED * KEG_LIFETIME;
/** Beer Goggles phase window, matching the v1 temporary phasing pickup. */
export const GOGGLES_TIME = 3;
/** House Round gives the owner a full boost and the rest of the live pack a tap. */
export const ROUND_SELF_BOOST_TIME = 3;
export const ROUND_PACK_BOOST_TIME = 0.9;
/** Corkscrew only reaches the next 30% of the current lap. */
export const CORKSCREW_TARGET_FRACTION = 0.3;
export const CORKSCREW_CONSOLATION_BOOST = 1.5;
/** Spill is a short, bounded puddle rather than a second Foam Slick alias. */
export const SPILL_LIFETIME = 10;
export const SPILL_RADIUS = 3.2;
export const SPILL_SPEED_LOSS = 0.6;

const ITEM_LAYOUTS: Record<RaceVariantState["itemLayout"], readonly (readonly number[])[]> = {
  "full-grid": ITEM_ROW_PROGRESS.map(() => ITEM_LANES),
  "split-rows": [ITEM_LANES, [-6, -2, 2, 6], ITEM_LANES, [-6, -2, 2, 6]],
  "offset-rows": [[-7, -3.5, 0, 3.5], [-3.5, 0, 3.5, 7], [-7, -3.5, 0, 3.5], [-3.5, 0, 3.5, 7]],
};

export function createItemBoxes(trackId = DEFAULT_TRACK_ID, variant?: Pick<RaceVariantState, "index" | "itemLayout">): ItemBoxState[] {
  const custom = getCustomTrackLayout(trackId);
  if (custom) return custom.itemProgress.flatMap((progress, row) => [-0.42, 0, 0.42].map((offset, lane) => {
    const distance = customTrackProgressToDistance(custom, progress);
    const lateral = offset * custom.halfWidth;
    const frame = sampleTrack(distance, trackId);
    return { id: `box-custom-${row}-${lane}`, distance, lateral,
      x: frame.position.x + frame.normal.x * lateral, y: frame.position.y + 1.05,
      z: frame.position.z + frame.normal.z * lateral, respawnAt: 0 };
  }));
  const trackLength = getTrackLength(trackId);
  const layout = ITEM_LAYOUTS[variant?.itemLayout ?? "full-grid"];
  return ITEM_ROW_PROGRESS.flatMap((progress, row) => layout[row].map((lateral, lane) => {
    const distance = trackLength * progress;
    const frame = sampleTrack(distance, trackId);
    return {
      id: `box-v${variant?.index ?? 0}-${row}-${lane}`,
      distance,
      lateral,
      x: frame.position.x + frame.normal.x * lateral,
      y: frame.position.y + 1.05,
      z: frame.position.z + frame.normal.z * lateral,
      respawnAt: 0,
    };
  }));
}

function itemRoll(kart: KartState, variantSeed: number): number {
  const kartNumber = Number(kart.id.replace(/\D/g, "")) + 1;
  // Avalanche each deterministic input so consecutive pickups visit the whole
  // roll range instead of creeping through one narrow LCG band per kart.
  let value = (Math.imul(kartNumber, 0x9e3779b1) ^ Math.imul(kart.itemRolls + 1, 0x85ebca6b) ^ Math.imul(variantSeed + 1, 0xc2b2ae35)) >>> 0;
  value ^= value >>> 16;
  value = Math.imul(value, 0x7feb352d) >>> 0;
  value ^= value >>> 15;
  value = Math.imul(value, 0x846ca68b) >>> 0;
  value ^= value >>> 16;
  kart.itemRolls += 1;
  return (value >>> 0) / 0x1_0000_0000;
}

export function chooseItemForRoll(place: number, fieldSize: number, roll: number): ItemKind {
  const trailing = fieldSize <= 1 ? 0 : (Math.max(1, Math.min(fieldSize, place)) - 1) / (fieldSize - 1);
  roll = Math.max(0, Math.min(0.999999, roll));
  if (trailing > 0.7) {
    return roll < 0.25 ? "six-pack"
      : roll < 0.37 ? "turbo"
        : roll < 0.44 ? "goggles"
          : roll < 0.52 ? "last-orders"
            : roll < 0.59 ? "round"
              : roll < 0.66 ? "shield"
                : roll < 0.72 ? "corkscrew"
                  : roll < 0.77 ? "keg"
                    : roll < 0.81 ? "foam"
                      : roll < 0.85 ? "spill"
                        : roll < 0.875 ? "bottle"
                          : roll < 0.925 ? "hiccups"
                            : "lime-wedge";
  }
  if (trailing < 0.22) {
    return roll < 0.24 ? "shield"
      : roll < 0.32 ? "foam"
        : roll < 0.38 ? "goggles"
          : roll < 0.44 ? "round"
            : roll < 0.49 ? "spill"
              : roll < 0.54 ? "corkscrew"
                : roll < 0.70 ? "keg"
                  : roll < 0.80 ? "turbo"
                    : roll < 0.855 ? "six-pack"
                      : roll < 0.935 ? "bottle"
                        : roll < 0.975 ? "hiccups"
                          : "lime-wedge";
  }
  return roll < 0.205 ? "turbo"
    : roll < 0.24 ? "goggles"
      : roll < 0.31 ? "six-pack"
        : roll < 0.37 ? "round"
          : roll < 0.45 ? "shield"
            : roll < 0.50 ? "corkscrew"
              : roll < 0.67 ? "keg"
                : roll < 0.75 ? "foam"
                  : roll < 0.79 ? "spill"
                    : roll < 0.82 ? "last-orders"
                      : roll < 0.89 ? "bottle"
                        : roll < 0.95 ? "hiccups"
                          : "lime-wedge";
}

function chooseItem(kart: KartState, fieldSize: number, variantSeed: number): ItemKind {
  return chooseItemForRoll(kart.place, fieldSize, itemRoll(kart, variantSeed));
}

function aheadTarget(race: RaceState, source: KartState, sameDeck = false, maxAheadDistance = Number.POSITIVE_INFINITY): KartState | null {
  return race.karts
    .filter((kart) => kart.id !== source.id
      && kart.finishedAt === null
      && kart.ghostTime <= 0
      && kart.distance > source.distance
      && kart.distance - source.distance <= maxAheadDistance
      && (!sameDeck || Math.abs(kart.y - source.y) <= 2.25))
    .sort((a, b) => a.distance - b.distance)[0] ?? null;
}

function corkscrewTarget(race: RaceState, source: KartState): KartState | null {
  const maxAheadDistance = race.trackLength * CORKSCREW_TARGET_FRACTION;
  return race.karts
    .filter((kart) => kart.id !== source.id
      && kart.finishedAt === null
      && kart.ghostTime <= 0
      && kart.heldItem !== null
      && kart.distance > source.distance
      && kart.distance - source.distance <= maxAheadDistance)
    .sort((a, b) => a.distance - b.distance || a.id.localeCompare(b.id))[0] ?? null;
}

export function hasHiccupsTarget(race: RaceState, source: KartState): boolean {
  return aheadTarget(race, source, true, race.trackLength * HICCUPS_MAX_TARGET_FRACTION) !== null;
}

/**
 * Try to apply the held item for this use command.
 *
 * A false result means that the command is still actionable but cannot be
 * applied on this tick (for example, the item lockout, hit spin, or a missing
 * Hiccups target is still active). Queued Twitch commands retain their
 * sequence so they can retry. Once the item is consumed, including an effect
 * absorbed by a target's immunity, this returns true and the sequence can be
 * acknowledged exactly once.
 */
function useItem(race: RaceState, kart: KartState): boolean {
  const item = kart.heldItem;
  // Lime Wedge is intentionally the exception: it is the recovery button a
  // player can press during the hit animation, rather than waiting for the
  // ordinary item lockout to finish.
  if (!item || kart.itemCooldown > 0 || (kart.spinTime > 0 && item !== "lime-wedge")) return false;
  // Hiccups is a direct rival effect rather than a physical projectile. Do not
  // consume it or emit a fake success presentation when there is no valid
  // same-deck racer ahead; the player can hold it until a real cast exists.
  const hiccupsTarget = item === "hiccups" ? aheadTarget(race, kart, true, race.trackLength * HICCUPS_MAX_TARGET_FRACTION) : null;
  if (item === "hiccups" && !hiccupsTarget) return false;
  kart.heldItem = null;
  kart.itemHeldAt = 0;
  kart.itemCooldown = 0.3;
  if (item === "turbo") {
    kart.boostTime = Math.max(kart.boostTime, 1.75);
    kart.itemDodgeTime = Math.max(kart.itemDodgeTime, 0.52);
    return true;
  }
  if (item === "six-pack") {
    kart.boostTime = Math.max(kart.boostTime, 3.4);
    kart.itemDodgeTime = Math.max(kart.itemDodgeTime, 0.72);
    return true;
  }
  if (item === "shield") {
    kart.shieldTime = Math.max(kart.shieldTime, 5);
    return true;
  }
  if (item === "lime-wedge") {
    kart.hiccupTime = 0;
    kart.spinTime = Math.min(kart.spinTime, 0.35);
    kart.boostTime = Math.max(kart.boostTime, LIME_BOOST_TIME);
    kart.itemDodgeTime = Math.max(kart.itemDodgeTime, LIME_DODGE_TIME);
    kart.itemImmunityTime = Math.max(kart.itemImmunityTime, LIME_IMMUNITY_TIME);
    return true;
  }
  if (item === "goggles") {
    kart.ghostTime = Math.max(kart.ghostTime, GOGGLES_TIME);
    // The immunity guard keeps direct status effects from slipping through on
    // the activation tick; collision/projectile/hazard paths also honor the
    // dedicated phase timer below.
    kart.itemImmunityTime = Math.max(kart.itemImmunityTime, GOGGLES_TIME);
    return true;
  }
  if (item === "round") {
    for (const rival of race.karts) {
      if (rival.finishedAt !== null) continue;
      rival.boostTime = Math.max(rival.boostTime, rival.id === kart.id ? ROUND_SELF_BOOST_TIME : ROUND_PACK_BOOST_TIME);
    }
    return true;
  }
  if (item === "spill") {
    const forwardX = Math.sin(kart.yaw); const forwardZ = Math.cos(kart.yaw);
    race.hazards.push({
      id: `spill-${kart.id}-${race.nextProjectileId++}`,
      kind: "spill",
      ownerId: kart.id,
      x: kart.x - forwardX * 3.1,
      y: kart.y - 0.02,
      z: kart.z - forwardZ * 3.1,
      radius: SPILL_RADIUS,
      distance: kart.distance - 3.1,
      lateral: kart.lateral,
      respawnAt: race.elapsed,
      expiresAt: race.elapsed + SPILL_LIFETIME,
    });
    kart.hazardCooldown = Math.max(kart.hazardCooldown, 0.85);
    return true;
  }
  if (item === "corkscrew") {
    const target = corkscrewTarget(race, kart);
    if (target?.heldItem) {
      const stolen = target.heldItem;
      target.heldItem = null;
      target.itemHeldAt = 0;
      target.pendingItemUse = false;
      target.itemStealCount += 1;
      target.lastItemStolen = stolen;
      kart.heldItem = stolen;
      kart.itemHeldAt = race.elapsed;
      return true;
    }
    kart.boostTime = Math.max(kart.boostTime, CORKSCREW_CONSOLATION_BOOST);
    return true;
  }
  if (item === "hiccups") {
    const target = hiccupsTarget!;
    if (target.itemImmunityTime > 0) return true;
    // A shield blocks this status and is consumed in the same way it blocks a
    // direct Keg impact.  Unlike Bottle, Hiccups has no projectile, so the
    // short dodge window is not spent on it.
    if (target.shieldTime > 0) {
      target.shieldTime = 0;
      return true;
    }
    target.hiccupTime = Math.max(target.hiccupTime, HICCUPS_DURATION);
    kart.itemHitCount += 1;
    kart.lastItemHit = "hiccups";
    return true;
  }
  if (item === "foam") {
    const forwardX = Math.sin(kart.yaw); const forwardZ = Math.cos(kart.yaw);
    race.hazards.push({
      id: `drop-${kart.id}-${race.nextProjectileId++}`,
      kind: "foam",
      ownerId: kart.id,
      x: kart.x - forwardX * 3.1,
      y: kart.y - 0.02,
      z: kart.z - forwardZ * 3.1,
      radius: 2.45,
      distance: kart.distance - 3.1,
      lateral: kart.lateral,
      respawnAt: race.elapsed,
      expiresAt: race.elapsed + 18,
    });
    kart.hazardCooldown = Math.max(kart.hazardCooldown, 1.1);
    return true;
  }
  if (item === "bottle") {
    const forwardX = Math.sin(kart.yaw);
    const forwardZ = Math.cos(kart.yaw);
    const maxDistance = race.trackLength * BOTTLE_MAX_TRAVEL_FRACTION;
    race.projectiles.push({
      id: race.nextProjectileId++,
      kind: "bottle",
      ownerId: kart.id,
      // Bottle is the skill-shot alternative to the homing Keg. It travels
      // exactly where the kart is aimed and can bank off two course rails.
      targetId: null,
      x: kart.x + forwardX * 2.3,
      y: kart.y + 0.55,
      z: kart.z + forwardZ * 2.3,
      velocityX: forwardX * BOTTLE_SPEED,
      velocityZ: forwardZ * BOTTLE_SPEED,
      expiresAt: race.elapsed + maxDistance / BOTTLE_SPEED + 0.2,
      distanceTravelled: 0,
      maxDistance,
      bounceCount: 0,
      maxBounces: BOTTLE_MAX_BOUNCES,
      previousDistance: kart.distance,
    });
    return true;
  }
  if (item === "last-orders") {
    for (const rival of race.karts) {
      if (rival.id === kart.id || rival.finishedAt !== null || rival.ghostTime > 0 || rival.distance <= kart.distance || rival.itemImmunityTime > 0) continue;
      if (rival.shieldTime > 0) rival.shieldTime = 0;
      else {
        rival.spinTime = Math.max(rival.spinTime, 1.3);
        rival.velocityX *= 0.42; rival.velocityZ *= 0.42;
        kart.itemHitCount += 1;
        kart.lastItemHit = "last-orders";
      }
    }
    return true;
  }
  // Over/under sections can put a rival metres away in plan view while they
  // are actually hundreds of route metres ahead on another deck. Bound the
  // lock to what the four-second homing flight can physically reach. Using a
  // raw height check would incorrectly reject legitimate targets on hills.
  const target = aheadTarget(race, kart, false, KEG_MAX_TARGET_DISTANCE);
  const forwardX = Math.sin(kart.yaw);
  const forwardZ = Math.cos(kart.yaw);
  race.projectiles.push({
    id: race.nextProjectileId++,
    kind: "keg",
    ownerId: kart.id,
    targetId: target?.id ?? null,
    x: kart.x + forwardX * 2.3,
    y: kart.y + 0.55,
    z: kart.z + forwardZ * 2.3,
    velocityX: forwardX * 58,
    velocityZ: forwardZ * 58,
    expiresAt: race.elapsed + 4,
  });
  return true;
}

export function stepKartItems(race: RaceState, kart: KartState, input: DriverInput, dt: number): void {
  kart.shieldTime = Math.max(0, kart.shieldTime - dt);
  kart.hiccupTime = Math.max(0, kart.hiccupTime - dt);
  kart.itemImmunityTime = Math.max(0, kart.itemImmunityTime - dt);
  kart.ghostTime = Math.max(0, kart.ghostTime - dt);
  kart.itemCooldown = Math.max(0, kart.itemCooldown - dt);
  const useSeq = Math.max(0, Math.floor(input.useSeq ?? 0));
  if (useSeq > kart.lastUseSeq) {
    const hadItem = kart.heldItem !== null;
    const queued = kart.pendingItemUse;
    const used = useItem(race, kart);
    // A sequence with no item, or an ordinary one-shot local/AI input, is
    // consumed immediately. A queued Twitch command remains pending while a
    // held item is blocked, and is acknowledged only when useItem accepts it.
    // This preserves local Hiccups' one-shot behavior while making Twitch
    // cooldown/spin retries reliable without double-fire.
    if (used || !hadItem || !queued) {
      kart.lastUseSeq = useSeq;
      kart.pendingItemUse = false;
    }
  }
  if (kart.heldItem) return;
  for (const box of race.itemBoxes) {
    if (box.respawnAt > race.elapsed || Math.abs(kart.y - box.y) > 2.5 || Math.hypot(kart.x - box.x, kart.z - box.z) > 2.35) continue;
    kart.heldItem = chooseItem(kart, race.karts.length, race.raceVariant.seed);
    kart.itemHeldAt = race.elapsed;
    box.respawnAt = race.elapsed + ITEM_RESPAWN;
    break;
  }
}

/**
 * Apply the Hiccups steering wobble at the same deterministic point in every
 * authoritative simulation.  It is a pure input transform, so human, AI and
 * Twitch karts receive identical control disruption for the same timestamp.
 */
export function applyHiccupsInput(kart: Pick<KartState, "id" | "hiccupTime">, input: DriverInput, time: number): DriverInput {
  if (kart.hiccupTime <= 0) return input;
  const seed = Number(kart.id.replace(/\D/g, "")) || 1;
  const wobble = Math.sin(time * 11.5 + seed * 1.37) * 0.14
    + Math.sin(time * 5.25 + seed * 0.73) * 0.07;
  return {
    ...input,
    steer: Math.max(-1, Math.min(1, input.steer + wobble)),
  };
}

/**
 * Predict only effects local to the driver while awaiting an authoritative
 * network snapshot. Projectiles, hazards, targets and pickups remain server
 * owned, so prediction cannot duplicate world entities or award an item.
 */
export function predictLocalItemStep(kart: KartState, input: DriverInput, dt: number, hiccupsTargetAvailable = true): void {
  kart.shieldTime = Math.max(0, kart.shieldTime - dt);
  kart.hiccupTime = Math.max(0, kart.hiccupTime - dt);
  kart.itemImmunityTime = Math.max(0, kart.itemImmunityTime - dt);
  kart.ghostTime = Math.max(0, kart.ghostTime - dt);
  kart.itemCooldown = Math.max(0, kart.itemCooldown - dt);
  const useSeq = Math.max(0, Math.floor(input.useSeq ?? 0));
  if (useSeq <= kart.lastUseSeq) return;
  // Local prediction keeps the input edge one-shot even when an item cannot
  // be applied yet (notably targetless Hiccups). The authoritative retry
  // contract above is only for queued Twitch commands.
  kart.lastUseSeq = useSeq;
  const item = kart.heldItem;
  if (!item || kart.itemCooldown > 0 || (kart.spinTime > 0 && item !== "lime-wedge")) return;
  if (item === "hiccups" && !hiccupsTargetAvailable) return;
  kart.heldItem = null;
  kart.itemHeldAt = 0;
  kart.itemCooldown = 0.3;
  if (item === "turbo") {
    kart.boostTime = Math.max(kart.boostTime, 1.75);
    kart.itemDodgeTime = Math.max(kart.itemDodgeTime, 0.52);
  } else if (item === "six-pack") {
    kart.boostTime = Math.max(kart.boostTime, 3.4);
    kart.itemDodgeTime = Math.max(kart.itemDodgeTime, 0.72);
  } else if (item === "shield") {
    kart.shieldTime = Math.max(kart.shieldTime, 5);
  } else if (item === "lime-wedge") {
    kart.hiccupTime = 0;
    kart.spinTime = Math.min(kart.spinTime, 0.35);
    kart.boostTime = Math.max(kart.boostTime, LIME_BOOST_TIME);
    kart.itemDodgeTime = Math.max(kart.itemDodgeTime, LIME_DODGE_TIME);
    kart.itemImmunityTime = Math.max(kart.itemImmunityTime, LIME_IMMUNITY_TIME);
  } else if (item === "goggles") {
    kart.ghostTime = Math.max(kart.ghostTime, GOGGLES_TIME);
    kart.itemImmunityTime = Math.max(kart.itemImmunityTime, GOGGLES_TIME);
  } else if (item === "round") {
    kart.boostTime = Math.max(kart.boostTime, ROUND_SELF_BOOST_TIME);
  } else if (item === "corkscrew") {
    // The stolen item and any world spill are authoritative; the local client
    // only predicts that the Corkscrew command was consumed.
  }
}

export function stepProjectiles(race: RaceState, dt: number): void {
  for (const projectile of race.projectiles) {
    const isBottle = projectile.kind === "bottle";
    if (isBottle && projectile.maxDistance === undefined) projectile.maxDistance = race.trackLength * BOTTLE_MAX_TRAVEL_FRACTION;
    if (isBottle && projectile.distanceTravelled === undefined) projectile.distanceTravelled = 0;
    const target = !isBottle ? race.karts.find((kart) => kart.id === projectile.targetId && kart.finishedAt === null && kart.ghostTime <= 0) : null;
    const projectileSpeed = isBottle ? BOTTLE_SPEED : KEG_HOMING_SPEED;
    if (target) {
      const dx = target.x - projectile.x;
      const dz = target.z - projectile.z;
      const distance = Math.max(0.001, Math.hypot(dx, dz));
      const desiredX = dx / distance * projectileSpeed;
      const desiredZ = dz / distance * projectileSpeed;
      const turn = 1 - Math.exp(-dt * 5.5);
      projectile.velocityX += (desiredX - projectile.velocityX) * turn;
      projectile.velocityZ += (desiredZ - projectile.velocityZ) * turn;
    }
    const currentSpeed = Math.max(0.001, Math.hypot(projectile.velocityX, projectile.velocityZ));
    const remainingDistance = isBottle && projectile.maxDistance !== undefined
      ? Math.max(0, projectile.maxDistance - (projectile.distanceTravelled ?? 0))
      : Number.POSITIVE_INFINITY;
    const moveDt = isBottle ? Math.min(dt, remainingDistance / currentSpeed) : dt;
    projectile.x += projectile.velocityX * moveDt;
    projectile.z += projectile.velocityZ * moveDt;
    if (isBottle) projectile.distanceTravelled = (projectile.distanceTravelled ?? 0) + currentSpeed * moveDt;
    const projectionY = Math.abs(projectile.y) < 50 ? projectile.y - 0.65 : undefined;
    const projection = projectToTrack(projectile.x, projectile.z, race.trackId, projectionY, projectile.previousDistance);
    projectile.y = projection.position.y + 0.65;
    if (isBottle) {
      projectile.previousDistance = projection.distance;
      if (Math.abs(projection.lateral) > projection.halfWidth) {
        const maxBounces = projectile.maxBounces ?? BOTTLE_MAX_BOUNCES;
        const bounceCount = projectile.bounceCount ?? 0;
        if (bounceCount >= maxBounces) {
          projectile.expiresAt = -1;
          continue;
        }
        const side = Math.sign(projection.lateral) || 1;
        const normalX = projection.normal.x * side;
        const normalZ = projection.normal.z * side;
        const outwardSpeed = projectile.velocityX * normalX + projectile.velocityZ * normalZ;
        if (outwardSpeed > 0) {
          projectile.velocityX -= 2 * outwardSpeed * normalX;
          projectile.velocityZ -= 2 * outwardSpeed * normalZ;
        }
        const bankLateral = side * Math.max(0, projection.halfWidth - 0.2);
        projectile.x = projection.position.x + projection.normal.x * bankLateral;
        projectile.z = projection.position.z + projection.normal.z * bankLateral;
        projectile.bounceCount = bounceCount + 1;
      }
    }

    // A deliberately dropped foam slick can guard the rear of the pack. A keg
    // and the slick are both consumed, giving the defender an active answer to
    // the warning instead of requiring a shield roll. Bottle stays narrow and
    // fast: it is answered by shield, dodge, or Lime immunity instead.
    const interceptedBy = !isBottle && race.hazards.find((hazard) => hazard.kind === "foam" && hazard.ownerId && hazard.respawnAt <= race.elapsed && (hazard.expiresAt === undefined || hazard.expiresAt > race.elapsed) && Math.abs(hazard.y - projectile.y) < 2.5 && Math.hypot(hazard.x - projectile.x, hazard.z - projectile.z) < hazard.radius + 0.7);
    if (interceptedBy) {
      interceptedBy.expiresAt = race.elapsed;
      const defender = race.karts.find((kart) => kart.id === interceptedBy.ownerId);
      if (defender) defender.itemGuardCount += 1;
      projectile.expiresAt = -1;
      continue;
    }

    const hit = race.karts.find((kart) => kart.id !== projectile.ownerId && kart.finishedAt === null && kart.ghostTime <= 0 && Math.abs(kart.y - projectile.y) < 2.5 && Math.hypot(kart.x - projectile.x, kart.z - projectile.z) < (isBottle ? BOTTLE_HIT_RADIUS : 2.2));
    if (hit) {
      if (hit.itemImmunityTime > 0) {
        // Lime's immunity is a timed guard, not a one-shot dodge. Let it keep
        // protecting the kart until its authoritative timer naturally expires.
      }
      else if (hit.shieldTime > 0) hit.shieldTime = 0;
      else if (hit.itemDodgeTime > 0) {
        hit.itemDodgeTime = 0;
        hit.kegDodgeCount += 1;
        hit.lastItemDodge = projectile.kind;
      }
      else {
        hit.spinTime = Math.max(hit.spinTime, isBottle ? BOTTLE_SPIN_TIME : 1.15);
        if (isBottle) hit.itemImmunityTime = Math.max(hit.itemImmunityTime, BOTTLE_IMMUNITY_TIME);
        hit.velocityX *= 0.35;
        hit.velocityZ *= 0.35;
        const owner = race.karts.find((kart) => kart.id === projectile.ownerId);
        if (owner) {
          owner.itemHitCount += 1;
          owner.lastItemHit = projectile.kind;
        }
      }
      projectile.expiresAt = -1;
    } else if (isBottle && (projectile.distanceTravelled ?? 0) >= (projectile.maxDistance ?? 0) - 1e-6) {
      projectile.expiresAt = -1;
    }
  }
  race.projectiles = race.projectiles.filter((projectile) => projectile.expiresAt > race.elapsed);
}
